/**
 * NutSheetPrintModal — date-range picker for the Nut Sheet Print PDF.
 *
 * Same shell as NutSheetExportModal (defaults to the 1st of the viewed month →
 * the viewed date, 366-day cap). On Print it fetches the range, applies the
 * page's row-visibility filters, renders a hidden landscape print view
 * (NutSheetPrintView), then drives the browser print dialog via the
 * `printing-nut-sheet` body class + @media print block in index.css — the
 * user "prints to PDF" or straight to paper from there. No summary/totals.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { ReportDateRangeSelector } from '../reports/ReportDateRangeSelector';
import { INDIVIDUAL_LOCATIONS } from '../LocationDropdown';
import { listNutSheetEntriesRange } from '../../services/nutSheetApi';
import { NutSheetEntry } from '../../types/nutSheet';
import {
  NutSheetExportFilters,
  applyRowFilters,
  describeFilters,
  sortNutSheetEntries,
} from '../../utils/nutSheetExport';
import { validateRange } from './NutSheetExportModal';
import { NutSheetPrintView } from './NutSheetPrintView';

interface NutSheetPrintModalProps {
  locationId: number;
  /** The date currently viewed on the page (YYYY-MM-DD) — seeds the default range. */
  viewedDate: string;
  excludeFiReserve: boolean;
  filters: NutSheetExportFilters;
  onClose: () => void;
}

interface PrintData {
  entries: NutSheetEntry[]; // filtered + sorted
  totalFetched: number;
  startDate: string;
  endDate: string;
}

export const NutSheetPrintModal: React.FC<NutSheetPrintModalProps> = ({
  locationId,
  viewedDate,
  excludeFiReserve,
  filters,
  onClose,
}) => {
  const [startDate, setStartDate] = useState(`${viewedDate.slice(0, 8)}01`);
  const [endDate, setEndDate] = useState(viewedDate);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printData, setPrintData] = useState<PrintData | null>(null);

  const locationName =
    INDIVIDUAL_LOCATIONS.find((l) => l.id === locationId)?.name ?? `Location ${locationId}`;
  const validationError = validateRange(startDate, endDate);

  // Once the print view is committed to the DOM, flip the body class and open
  // the print dialog. afterprint (fires on print AND cancel) cleans up; the
  // modal stays open so another range can be printed.
  useEffect(() => {
    if (!printData) return;
    const cleanup = () => {
      document.body.classList.remove('printing-nut-sheet');
      setPrintData(null);
    };
    document.body.classList.add('printing-nut-sheet');
    window.addEventListener('afterprint', cleanup, { once: true });
    window.print();
    return () => window.removeEventListener('afterprint', cleanup);
  }, [printData]);

  const handlePrint = async () => {
    if (validationError) return;
    setLoading(true);
    setError(null);
    try {
      const response = await listNutSheetEntriesRange(
        locationId,
        startDate,
        endDate,
        excludeFiReserve,
      );
      if (!response.success || !response.data) {
        setError(response.error || 'Failed to load entries for the selected range.');
        return;
      }
      const visible = sortNutSheetEntries(applyRowFilters(response.data.entries, filters));
      if (visible.length === 0) {
        setError(
          response.data.entries.length === 0
            ? 'No entries in this date range.'
            : 'No entries in this range match the current row filters.',
        );
        return;
      }
      setPrintData({
        entries: visible,
        totalFetched: response.data.entries.length,
        startDate,
        endDate,
      });
    } catch (e: any) {
      setError(e?.message || 'Print failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900">Print PDF</h3>
        <p className="mt-1 text-sm text-gray-600">
          {locationName} — choose the date range to print.
        </p>

        <div className="mt-4">
          <ReportDateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            disabled={loading}
          />
        </div>

        <div className="mt-4 rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700 space-y-1">
          <p>
            <span className="font-semibold">F&amp;I Reserve:</span>{' '}
            {excludeFiReserve
              ? 'Excluded — each row shows F&I, Reserve, and Net (F&I − Reserve)'
              : 'Included — full F&I figures'}
          </p>
          <p>
            <span className="font-semibold">Row filters:</span> {describeFilters(filters)}
          </p>
          <p className="text-gray-500 italic">
            The printout is a landscape listing of the deal rows only — no summary cards or
            totals. Change the toggles on the page before printing to adjust either setting.
          </p>
        </div>

        {validationError && startDate && endDate && (
          <p className="mt-3 text-sm text-red-600">{validationError}</p>
        )}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={loading || !!validationError}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${
              loading || validationError
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <PrinterIcon className="h-4 w-4" />
            {loading ? 'Loading…' : 'Print'}
          </button>
        </div>
      </div>

      {/* Portaled to <body>: a multi-page printout inside the modal's
          position:fixed overlay would be clipped to one page (same reason
          DealDetailsModal clones its content to a top-level wrapper). */}
      {printData &&
        createPortal(
          <div id="nut-sheet-print-container" className="hidden">
            <NutSheetPrintView
              locationName={locationName}
              startDate={printData.startDate}
              endDate={printData.endDate}
              entries={printData.entries}
              totalFetched={printData.totalFetched}
              excludeFiReserve={excludeFiReserve}
              filters={filters}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};
